import { MoreMenuIcon } from "@taunggyi-plus/design-system";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
type Notice = {
  id: string;
  title: string;
  body: string;
  deep_link: string;
  read_at: string | null;
};
export function useOrderActivity() {
  const { client, user } = useAuth();
  const [state, setState] = useState<{ user: string; unread: number; arrivals: Notice[] }>({
    user: "",
    unread: 0,
    arrivals: [],
  });
  const [sound, setSound] = useState(false);
  const [soundError, setSoundError] = useState("");
  const audio = useRef<HTMLAudioElement | null>(null);
  const enabled = useRef(sound);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    enabled.current = sound;
  }, [sound]);
  useEffect(() => {
    if (!user || !client) return;
    let active = true,
      running = false;
    let seen: Set<string> | null = null;
    const load = async () => {
      if (running || document.visibilityState === "hidden") return;
      running = true;
      try {
        const result = await client
          .from("notifications")
          .select("id,title,body,deep_link,read_at")
          .eq("recipient_user_id", user.id)
          .order("created_at", { ascending: false });
        if (!active || result.error) return;
        const rows = (result.data ?? []) as Notice[];
        const incoming = seen
          ? rows.filter(
              (n) =>
                !seen!.has(n.id) &&
                !n.read_at &&
                /^\/(orders|bookings|jobs|rides|delivery)/.test(n.deep_link ?? ""),
            )
          : [];
        seen = new Set(rows.map((n) => n.id));
        setState((current) => ({
          user: user.id,
          unread: rows.filter((n) => !n.read_at).length,
          arrivals: [...(current.user === user.id ? current.arrivals : []), ...incoming],
        }));
        if (incoming.length) {
          window.dispatchEvent(new Event("taunggyi:orders-updated"));
          if (enabled.current && audio.current) {
            audio.current.currentTime = 0;
            void audio.current
              .play()
              .catch(() =>
                setSoundError("Sound is blocked. Enable sound again; visual alerts remain active."),
              );
          }
        }
      } finally {
        running = false;
      }
    };
    const run = () => {
      void load().catch(() => {});
    };
    run();
    const timer = window.setInterval(run, 10000);
    document.addEventListener("visibilitychange", run);
    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
    };
  }, [client, user]);
  const arrivals = state.user === user?.id ? state.arrivals : [];
  useEffect(() => {
    if (arrivals.length) dialog.current?.showModal();
    else dialog.current?.close();
  }, [arrivals.length]);
  const dismiss = () => setState((s) => ({ ...s, arrivals: [] }));
  const control = (
    <div>
      <button
        className="more-menu-row"
        type="button"
        onClick={() => {
          if (sound) {
            setSound(false);
            audio.current?.pause();
            return;
          }
          if (!audio.current)
            audio.current = new Audio(new URL("./order-alert.wav", import.meta.url).href);
          audio.current.currentTime = 0;
          void audio.current
            .play()
            .then(() => {
              setSound(true);
              setSoundError("");
            })
            .catch(() => setSoundError("Sound is unavailable. Visual alerts remain active."));
        }}
      >
        <MoreMenuIcon name="sound" />
        <strong>{sound ? "Mute order alert sound" : "Enable order alert sound"}</strong>
        <b aria-hidden="true">›</b>
      </button>
      {soundError && <p role="alert">{soundError}</p>}
    </div>
  );
  return {
    unread: state.user === user?.id ? state.unread : 0,
    control,
    popup: (
      <dialog
        ref={dialog}
        className="customer-order-filter-dialog subscription-popup sign-out-dialog"
        aria-label="Order update"
        onCancel={dismiss}
      >
        <h3>{arrivals[0]?.title ?? "Order update"}</h3>
        <p role="alert">{arrivals[0]?.body}</p>
        {arrivals.length > 1 && <p>{arrivals.length} new order updates</p>}
        <div style={{ display: "grid", gap: 12 }}>
          <button
            className="button"
            type="button"
            onClick={() => {
              dismiss();
              window.location.assign("/orders");
            }}
          >
            View orders
          </button>
          <button className="button button--secondary" type="button" onClick={dismiss}>
            Dismiss
          </button>
          {control}
        </div>
      </dialog>
    ),
  };
}
